import { Filter, MultiFilter, SetFilter } from '@helios/server-side-util'

/**
 * Typed mirror of the Flex Positions read API as it stands after PVAL-26116 (backend MR !468),
 * matching the backend's FlexPositionResource:
 *
 *  - POST /flex-positions/query?source=<ENFUSION|PROMETHEUS> — the source moved OUT of the body
 *    and onto the query string. A missing or unknown value is a 400, so {@link QUERY_SOURCE} is the
 *    only place a SourceKey is allowed to become one.
 *  - GET  /flex-positions/fields?accountId=..&sources=.. — the metadata catalog that supplies the
 *    per-column {@link ColumnType} the request below has to echo back.
 *
 * `helios-session-id` is required on /query (a missing or unknown one is 410 Gone, NOT 500) and is
 * attached globally by the app's SessionInterceptor, so no call site sets it. /fields no longer
 * reads it; the interceptor still sends it there and that is harmless, so nothing is unwired.
 */

/** Backend's FlexPositionSource enum. Clearwater has no read endpoint, so it is never a valid
 *  value here. */
export type FlexPositionSource = 'ENFUSION' | 'PROMETHEUS'

/**
 * The `filterType` vocabulary, used on every entry of the request's flat `filter` map.
 *
 * Deliberately identical to `@helios/server-side-util`'s {@link FilterType} enum values, which is
 * why the house `convertFilters` output drops straight into {@link FlexPositionsQueryRequest.filter}
 * with no re-shaping. Omitting it makes the backend fall back to a TEXT comparison, which is how a
 * numeric column ends up sorting 1000 before 9 — so it is always sent.
 */
export type QueryFilterType =
  | 'number'
  | 'date'
  | 'datetime'
  | 'time'
  | 'text'
  | 'set'

/**
 * The `type` vocabulary on a sort entry. A SECOND, disjoint spelling of the same concept as
 * {@link QueryFilterType} — the contract really does use two vocabularies, so the two are mapped
 * explicitly (see `grid/query/field-types.ts`) rather than upper-cased into each other.
 */
export type QuerySortType =
  | 'NUM'
  | 'DATE'
  | 'DATETIME'
  | 'TIME'
  | 'BOOL'
  | 'TEXT'

/**
 * One filter-map entry. Structurally the house `Filter` family
 * (`{ filterType, operator, filters[] }` for the scalar types, `{ filterType: 'set', values[] }`
 * for a set), which the contract's own example matches key for key.
 *
 * A UNION rather than the bare `Filter` base so an object literal for either concrete shape type-checks
 * — `Filter` alone declares only `filterType`, so TypeScript's excess-property check rejects the
 * `values` / `filters` a real entry has to carry.
 */
export type QueryFilter = Filter | SetFilter | MultiFilter<unknown>

export interface QuerySort {
  field: string
  direction: 'ASC' | 'DESC'
  /** Always populated — see {@link QuerySortType}. */
  type: QuerySortType
}

/**
 * `context.fieldStorage.aggInfo` — every AGG_INFO-storage field the request touches, across
 * `fields` + `filter` + `sort`. Optional in the contract but recommended: supplying it saves the
 * backend a per-query Redis lookup. An empty array means "this request touches none", which is
 * NOT the same as omitting `context` altogether (that asks the backend to resolve it itself —
 * always correct, just slower). We always send the list, so the empty case is `[]`.
 */
export interface QueryFieldStorageContext {
  aggInfo: string[]
}

export interface QueryRequestContext {
  fieldStorage: QueryFieldStorageContext
}

/** Request body for POST /flex-positions/query. */
export interface FlexPositionsQueryRequest {
  /** Inclusive. */
  startRow: number
  /** Exclusive. */
  endRow: number
  /**
   * Projection. A named pos_data key is hoisted to the top level of each row
   * (`data[0].positionInstrumentId.value`); omitting `fields` instead returns the real columns plus
   * whole `posData` / `aggInfo` / `matchCounts` / `matchedIds` blobs. We always project so the
   * response stays proportional to what is on screen.
   */
  fields: string[]
  /**
   * ONE flat map keyed by field name — real columns and JSON keys side by side. There is no
   * `dynamicFilters` / `aggFilters` split any more.
   */
  filter: Record<string, QueryFilter>
  sort: QuerySort[]
  context: QueryRequestContext
}

/**
 * Response envelope. `data` is wrapped, `gridLoadStatus` is gone, and NULLS ARE OMITTED — an absent
 * key means no value, so every read of it goes through `row.adapter.ts` rather than assuming a
 * key exists.
 */
export interface GridResponse {
  data?: Array<Record<string, unknown>>
}

/** The `error` discriminator on the 400 body this UI has to attribute per-column. */
export const INVALID_FIELD_TYPE = 'INVALID_FIELD_TYPE'

/**
 * The 400 body for a chosen type Postgres could not apply.
 *
 * `fields` is a LIST, not a single column: Postgres reports the offending *value*, not the column it
 * came from, so the backend names every field that claimed the failing type. `value` is the value
 * that could not be read — the known trigger is Prometheus sending an empty string for a field that
 * does not apply to an instrument, so a numeric filter over a sometimes-blank column 400s.
 */
export interface InvalidFieldTypeBody {
  error?: string
  fields?: string[]
  requestedType?: string
  value?: unknown
  message?: string
}

/**
 * One source's slice of a by-ids read. `rowIds` are entity ids as they arrived on a `/query` row.
 *
 * Note the backend scopes `findByIds` by `(sessionId, recordSource, rowId IN …)` and NOT by
 * account, and a position rowId is not unique across accounts (FlexMatchRowKey's doc comment) — so
 * one requested id can legitimately come back as several rows. Callers must reconcile on
 * `(rowId, accountId)`, never on `rowId` alone.
 */
export interface FlexPositionByIdsRequestItem {
  source: FlexPositionSource
  rowIds: string[]
}

/**
 * Request body for POST /flex-positions/by-ids. Takes no `fields` argument — the endpoint returns
 * the DAO's fixed result map — so the `fields`-400 quirk noted on {@link DataRequest} does not
 * apply here.
 */
export interface FlexPositionByIdsRequest {
  requests: FlexPositionByIdsRequestItem[]
}
